import type { useToast } from "@chakra-ui/react";
import type { LaunchCampaignRequestBody } from "@common/interfaces/adsManagerGuidedEdit";
import type { GuidedCreateCampaignParams } from "../guidedFlow/types";
import actions from "src/actions";
import {
  mapGuidedTargetingToLaunchWire,
  wireFormBudgetToLaunchCampaignWire,
  wireFormScheduleToLaunchSchedule,
} from "./guidedCampaignLaunchWire";
import { mapCreativeBlockToGuidedCreative } from "./mapCreativeBlockToGuidedCreative";

export type SaveDraftCampaignParams = GuidedCreateCampaignParams & {
  campaign_id?: string;
};

function hasScheduleValues(schedule: GuidedCreateCampaignParams["schedule"]): boolean {
  return Boolean(schedule?.start_at?.trim() && schedule?.end_at?.trim());
}

export function buildSaveDraftCampaignWire(
  params: SaveDraftCampaignParams
): Partial<LaunchCampaignRequestBody> & { event_id: string } {
  const body: Partial<LaunchCampaignRequestBody> & { event_id: string } = {
    event_id: params.event_id,
    ad_account_id: params.ad_account_id,
  };

  if (params.budget && Object.keys(params.budget).length > 0) {
    body.budget = wireFormBudgetToLaunchCampaignWire(
      params.budget,
      params.eventTimezone
    );
  }

  if (hasScheduleValues(params.schedule)) {
    body.schedule = wireFormScheduleToLaunchSchedule(
      params.schedule,
      params.eventTimezone
    );
  }

  if (params.creatives?.length) {
    body.creatives = params.creatives.map((creative) =>
      mapCreativeBlockToGuidedCreative(creative, {
        partnerPostsEnabled: params.partnerPostsEnabled,
      })
    );
  }

  if (params.targeting) {
    body.targeting = mapGuidedTargetingToLaunchWire(params.targeting);
  }

  return body;
}

export type SaveDraftCampaignResult = {
  campaignId: string;
  status: string;
};

export async function saveDraftCampaignRequest(
  params: SaveDraftCampaignParams,
  deps: { toast: ReturnType<typeof useToast> }
): Promise<SaveDraftCampaignResult | null> {
  const wire = buildSaveDraftCampaignWire(params);
  const campaignId = params.campaign_id?.trim();
  const response = (campaignId
    ? await actions.ADMIN.saveDraftCampaignUpdate(campaignId, wire)
    : await actions.ADMIN.saveDraftCampaignCreate(wire)) as {
    campaign_id?: string;
    status?: string;
  };

  if (!response?.campaign_id) {
    deps.toast({
      title: "Failed to save draft",
      description: "The server did not return a campaign id.",
      status: "error",
    });
    return null;
  }

  return {
    campaignId: response.campaign_id,
    status: response.status ?? "DRAFT",
  };
}
